/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { site } from '@panel/commons/helpers';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@panel/commons/hooks';
import {
  selectTaxConditions,
  selectBillingInfo,
  updateProductTaxes,
  selectCurrentProduct,
  getPaymentLoadingStatus
} from '../../../../../../ducks/step4Module2';
import { Step4Module1Selectors } from '../../../../../../ducks/step4Module1';
import { Title, FieldRow, FieldError, PaymentFieldSet } from '../../../styles';
import { InputField, SelectField } from '../fields';
import BillingLocation from './location';
import { getWithUpgrade } from '../../../../../../utils/misc';

const includeLocation = site.isBrasil || site.isPeru;

function BillingEditable() {
  const [withUpgrade, setWithUpgrade] = useState(false);
  const { legalName, ecommerceTaxId, taxCondition, address, postalCode } = useSelector(selectBillingInfo);
  const { id: productId, priceList: userType } = useSelector(selectCurrentProduct);
  const { taxConditionOpts, taxConditions } = useSelector(selectTaxConditions);
  const { currentPlan, eligibleForUpgrade, products } = useSelector(Step4Module1Selectors.getStep4Module1State);
  const loading = useSelector(getPaymentLoadingStatus);
  const [postingId] = useQuery(useLocation(), ['postingId']);
  const { watch, reset, getValues, clearErrors, trigger } = useFormContext();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const currentPlanId = eligibleForUpgrade && currentPlan && withUpgrade ? currentPlan.publication_plan_id : '';
  const watchedFields = watch(['userName', 'userDocument', 'userTaxCondition', 'address', 'postalCode']);
  const renderedFields = Object.keys(watchedFields).filter(
    field => watchedFields[field] !== undefined && watchedFields[field] !== null
  );

  useEffect(() => {
    if (eligibleForUpgrade && products.length > 0 && !withUpgrade) {
      setWithUpgrade(getWithUpgrade(products));
    }
  }, [products, eligibleForUpgrade, withUpgrade]);

  useEffect(() => {
    if (taxConditions.length > 0) {
      reset({
        userName: /^(?!. .).*$/.test(legalName) ? legalName : '',
        userDocument: ecommerceTaxId,
        userTaxCondition: taxConditions.some(value => value.id === taxCondition) ? taxCondition : taxConditions[0].id
      });
    }
  }, [taxConditions, legalName, ecommerceTaxId, reset, taxCondition]);

  const shouldUpdateTaxes = () => {
    let result = false;
    const formValues = getValues(renderedFields);
    const defaultValues = {
      userName: legalName,
      usertaxCondition: taxCondition,
      userDocument: ecommerceTaxId,
      address,
      postalCode
    };

    Object.keys(formValues).forEach(field => {
      if (formValues[field] !== '') {
        if (formValues[field] !== defaultValues[field]) {
          result = true;
        }
      }
    });
    return result;
  };

  const updateTaxes = async () => {
    if (shouldUpdateTaxes()) {
      clearErrors();
      const val = await trigger(renderedFields);
      const billingData = {
        legalName: watchedFields.userName,
        ecommerceTaxId: watchedFields.userDocument,
        taxCondition: watchedFields.usertaxCondition,
        postalCode: watchedFields.postalCode,
        address: watchedFields.address
      };
      if (val) {
        let query = { products: productId, userType, currentPlanId, postingId };
        const coupon = getValues('coupon');
        if (coupon) {
          query = { ...query, coupon };
        }
        dispatch(updateProductTaxes(billingData, query));
      }
    }
  };

  const setTaxConditionLabel = () => {
    const userTaxId = taxConditions && taxConditions.find(condition => condition.id === watchedFields.userTaxCondition);
    return userTaxId && watchedFields.userTaxCondition
      ? userTaxId.description
      : t('publisher.step4.module2.field.userDocument.label');
  };

  return (
    <PaymentFieldSet disabled={loading}>
      <Title subtitle>{t('publisher.step4.module2.billing.subtitle')}</Title>
      <FieldRow>
        <InputField
          name='userName'
          onBlur={updateTaxes}
          renderError={errorProps => (
            <ErrorMessage {...errorProps} render={({ message }) => <FieldError>{t(message)}</FieldError>} />
          )}
        />
      </FieldRow>
      <FieldRow>
        <SelectField
          name='userTaxCondition'
          options={taxConditionOpts}
          onChange={updateTaxes}
          renderError={errorProps => (
            <ErrorMessage {...errorProps} render={({ message }) => <FieldError>{t(message)}</FieldError>} />
          )}
        />
        <InputField
          name='userDocument'
          label={setTaxConditionLabel()}
          onBlur={updateTaxes}
          renderError={errorProps => (
            <ErrorMessage {...errorProps} render={({ message }) => <FieldError>{t(message)}</FieldError>} />
          )}
        />
      </FieldRow>
      {includeLocation && <BillingLocation updateTaxes={updateTaxes} />}
    </PaymentFieldSet>
  );
}

export default BillingEditable;
